import { useCallback, useEffect, useState } from 'react'
import { useFormContext, type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SparklesIcon } from 'lucide-react'
import {
	Button,
	FileDropzone,
	FileUploadCard, type FileUploadState,
	FormControl, FormField, FormItem, FormLabel, FormMessage,
	Input,
	Textarea,
} from '@rentflowdev/ui'
import { useGenerateUploadUrlMutation } from '@/api'
import type { AddPropertyFormValues } from './schema'
import { SectionHeading } from './section-heading'

interface StepProps {
	control: Control<AddPropertyFormValues>
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_FILE_SIZE  = 10 * 1024 * 1024
const MAX_PHOTOS     = 40

interface UploadingFile {
	id:       string
	name:     string
	size:     number
	preview:  string
	state:    FileUploadState
	progress: number
	error?:   string
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} Б`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`
	return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

export function Step6PhotosDescription({ control }: StepProps) {
	const { t } = useTranslation()
	const form = useFormContext<AddPropertyFormValues>()
	const [generateUploadUrl] = useGenerateUploadUrlMutation()
	const [uploads, setUploads] = useState<UploadingFile[]>([])

	const images = form.watch('images') ?? []

	const upload = useCallback(async (file: File) => {
		const id = crypto.randomUUID()
		const preview = URL.createObjectURL(file)
		setUploads((q) => [...q, { id, name: file.name, size: file.size, preview, state: 'loading', progress: 0 }])

		try {
			const { uploadUrl, publicUrl } = await generateUploadUrl({
				contentType: file.type,
				fileName:    file.name,
			}).unwrap()

			await new Promise<void>((resolve, reject) => {
				const xhr = new XMLHttpRequest()
				xhr.open('PUT', uploadUrl)
				xhr.setRequestHeader('Content-Type', file.type)
				xhr.upload.onprogress = (e) => {
					if (!e.lengthComputable) return
					const progress = Math.round((e.loaded / e.total) * 100)
					setUploads((q) => q.map((u) => (u.id === id ? { ...u, progress } : u)))
				}
				xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)))
				xhr.onerror = () => reject(new Error('Network error'))
				xhr.send(file)
			})

			form.setValue('images', [...form.getValues('images') ?? [], publicUrl], { shouldDirty: true })
			// Из очереди — вон: дальше файл живёт в `images`, и запись, оставшаяся
			// со статусом «загружено», рисовала его вторым разом, под именем из
			// адреса. Список загрузок показывает то, что ещё идёт или упало.
			URL.revokeObjectURL(preview)
			setUploads((q) => q.filter((u) => u.id !== id))
		} catch (err) {
			setUploads((q) => q.map((u) => (u.id === id ? { ...u, state: 'error', error: (err as Error).message } : u)))
		}
	}, [form, generateUploadUrl])

	const handleFiles = useCallback((files: Iterable<File>) => {
		for (const file of Array.from(files)) {
			if (!ACCEPTED_TYPES.includes(file.type)) continue
			if (file.size > MAX_FILE_SIZE) continue
			void upload(file)
		}
	}, [upload])


	const removeImage = (url: string) => {
		form.setValue('images', (form.getValues('images') ?? []).filter((u) => u !== url), { shouldDirty: true })
	}

	const removeUpload = (id: string) => {
		const target = uploads.find((u) => u.id === id)
		if (target?.preview) URL.revokeObjectURL(target.preview)
		setUploads((q) => q.filter((u) => u.id !== id))
	}

	const totalCount = images.length + uploads.filter((u) => u.state !== 'error').length
	const reachedMax = totalCount >= MAX_PHOTOS

	/**
	 * Снимок экрана попадает в объявление вставкой, а не через «Сохранить как».
	 *
	 * Слушаем окно, а не поле: вставлять хочется сразу после копирования, не
	 * целясь предварительно в область загрузки. Текст при этом не трогаем —
	 * реагируем, только если в буфере действительно есть картинки, поэтому
	 * вставка в описание работает как прежде.
	 */
	useEffect(() => {
		if (reachedMax) return
		const onPaste = (e: ClipboardEvent) => {
			const images = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'))
			if (images.length === 0) return
			e.preventDefault()
			handleFiles(images)
		}
		window.addEventListener('paste', onPaste)
		return () => window.removeEventListener('paste', onPaste)
	}, [reachedMax, handleFiles])

	return (
		<div className="flex flex-col gap-5 px-4 pt-2 pb-8">
			<section className="flex flex-col gap-2">
				<SectionHeading>{t('properties.photos.title')}</SectionHeading>
				<p className="text-sm leading-5 font-[400] text-(--color-foreground)">
					{t('properties.photos.subtitle')}
				</p>

				<FileDropzone
					accept={ACCEPTED_TYPES.join(',')}
					multiple
					disabled={reachedMax}
					dropLabel={t('properties.photos.dropLabel')}
					selectLabel={t('properties.photos.selectLabel')}
					hint={t('properties.photos.hint')}
					onFilesSelected={handleFiles}
				/>

				{(images.length > 0 || uploads.length > 0) && (
					<div className="flex flex-col gap-2">
						{images.map((url) => (
							<FileUploadCard
								key={url}
								state="uploaded"
								fileName={url.split('/').pop() ?? url}
								fileSize=""
								previewSrc={url}
								removeLabel={t('common.delete')}
								onRemove={() => removeImage(url)}
							/>
						))}
						{uploads.map((u) => (
							<FileUploadCard
								key={u.id}
								state={u.state}
								fileName={u.name}
								fileSize={formatSize(u.size)}
								previewSrc={u.preview}
								progress={u.progress}
								loadingLabel={`${u.progress}%`}
								errorLabel={u.error}
								removeLabel={t('common.remove')}
								onRemove={() => removeUpload(u.id)}
							/>
						))}
					</div>
				)}
			</section>

			<FormField
				control={control}
				name="videoUrl"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t('properties.photos.video')}</FormLabel>
						<FormControl>
							<Input placeholder={t('properties.photos.videoPlaceholder')} {...field} value={field.value ?? ''} />
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			<section className="flex flex-col gap-2">
				<SectionHeading>{t('properties.photos.description')}</SectionHeading>
				<FormField
					control={control}
					name="description"
					render={({ field }) => (
						<FormItem>
							<FormControl>
								<Textarea
									placeholder={t('properties.photos.descriptionPlaceholder')}
									className="min-h-20"
									{...field}
									value={field.value ?? ''}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<Button
					type="button"
					variant="link"
					size="sm"
					iconLeft={<SparklesIcon className="size-4" />}
					disabled
					title={t('properties.photos.aiComingSoon')}
					className="h-auto p-0 self-start text-(--color-brand)"
				>
					{t('properties.photos.generateDescription')}
				</Button>
			</section>
		</div>
	)
}
